import { DateTypeFilter } from '@gripp/shared-logic';
import { Colors, DateRangeSelector, FormContext } from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { RadioButton, Text } from 'react-native-paper';
import { mixed, string, object as yupObject } from 'yup';

interface DataExportModalProps {
  visible: boolean;
  onDismiss: () => void;
  onDownload: (filter: {
    dateType: DateTypeFilter;
    startDate?: string;
    endDate?: string;
  }) => void;
  initialFilter?: {
    dateType: DateTypeFilter;
    startDate?: string;
    endDate?: string;
  };
}

type FormData = {
  dateType: DateTypeFilter;
  startDate?: string;
  endDate?: string;
};

export const DataExportModal = ({
  visible,
  onDismiss,
  onDownload,
  initialFilter,
}: DataExportModalProps) => {
  const { t } = useTranslation();

  const formSchema = yupObject({
    dateType: mixed<DateTypeFilter>()
      .oneOf(Object.values(DateTypeFilter))
      .required(),
    startDate: string().when('dateType', (dateType, schema) => {
      const value = Array.isArray(dateType) ? dateType[0] : dateType;
      return value === DateTypeFilter.CUSTOMDATE ? schema.required() : schema;
    }),
    endDate: string().when('dateType', (dateType, schema) => {
      const value = Array.isArray(dateType) ? dateType[0] : dateType;
      return value === DateTypeFilter.CUSTOMDATE ? schema.required() : schema;
    }),
  });

  const formMethods = useForm<FormData>({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: {
      dateType: initialFilter?.dateType || DateTypeFilter.ALL,
      startDate: initialFilter?.startDate || '',
      endDate: initialFilter?.endDate || '',
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
    setValue,
    watch,
  } = formMethods;

  const formData = watch();
  const selectedDate = watch('dateType');

  const resetForm = useCallback(() => {
    reset({ dateType: DateTypeFilter.ALL });
  }, [reset]);

  const onReset = async () => {
    resetForm();
  };

  const onSubmit = async (data: FormData) => {
    onDownload(data);
    onDismiss();
  };

  const canReset = useCallback(() => {
    const date = formData.dateType;
    const defaultFilter = {
      dateType: DateTypeFilter.ALL,
      startDate: '',
      endDate: '',
    };

    return date !== defaultFilter.dateType;
  }, [formData]);

  useEffect(() => {
    if (!initialFilter) {
      resetForm();
    }
  }, [initialFilter, resetForm]);

  const onDateChange = (date: DateTypeFilter) => {
    if (
      date !== DateTypeFilter.CUSTOMDATE &&
      (control._formValues.endDate || control._formValues.startDate)
    ) {
      setValue('startDate', '');
      setValue('endDate', '');
    }
    setValue('dateType', date, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const renderCustomDate = (value: DateTypeFilter) => {
    if (value !== DateTypeFilter.CUSTOMDATE) return null;

    return (
      <View style={styles.renderCustomDate}>
        <DateRangeSelector
          control={control}
          setValue={setValue}
          styleContainer={styles.dateRangeContainer}
        />
      </View>
    );
  };

  if (!visible) return null;

  return (
    <FormContext.Provider value={formMethods as any}>
      {/* Custom Header with Cancel on Left */}
      <View style={styles.header}>
        <Pressable onPress={onDismiss} style={styles.cancelButton}>
          <Text style={styles.cancelButtonText}>
            {t('accountPage.dataExport.cancel')}
          </Text>
        </Pressable>
        <Text style={styles.title}>{t('accountPage.dataExport.title')}</Text>
        <View style={styles.headerSpacer} />
      </View>

      <View style={styles.container}>
        <View style={[styles.scrollViewContainer, { height: 530 }]}>
          <ScrollView
            contentContainerStyle={styles.scrollViewContent}
            scrollEnabled={true}
            showsVerticalScrollIndicator={true}
          >
            <View>
              {Object.entries(DateTypeFilter).map(([key, value]) => {
                const enumKey = key as keyof typeof DateTypeFilter;
                return (
                  <Pressable
                    key={enumKey}
                    onPress={() => onDateChange(DateTypeFilter[enumKey])}
                  >
                    <View style={styles.dateFilterRowRadioButton}>
                      <View
                        style={[
                          styles.rowDateFilterContainer,
                          styles.rowRadioButton,
                        ]}
                      >
                        <Text numberOfLines={1} style={styles.checkboxLabel}>
                          {t(`activity.filter.${value}`)}
                        </Text>
                        <RadioButton.Android
                          value={DateTypeFilter[enumKey]}
                          status={
                            DateTypeFilter[enumKey] === selectedDate
                              ? 'checked'
                              : 'unchecked'
                          }
                          onPress={() => onDateChange(DateTypeFilter[enumKey])}
                        />
                      </View>
                      {renderCustomDate(value)}
                    </View>
                  </Pressable>
                );
              })}
            </View>
          </ScrollView>
        </View>

        {/* Download Button */}
        <View style={styles.downloadButtonContainer}>
          <Pressable
            style={styles.downloadButton}
            onPress={handleSubmit(onSubmit)}
          >
            <Text style={styles.downloadButtonText}>
              {t('accountPage.dataExport.download')}
            </Text>
          </Pressable>
        </View>
      </View>
    </FormContext.Provider>
  );
};

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.graySpacer,
    backgroundColor: Colors.white,
  },
  cancelButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  cancelButtonText: {
    color: Colors.primaryDark,
    fontSize: 16,
    fontWeight: '500',
  },
  title: {
    fontSize: 16,
    fontWeight: '700',
    color: Colors.blackText,
    textAlign: 'center',
  },
  headerSpacer: {
    width: 60, // Same width as cancel button for centering
  },
  container: {
    flex: 1,
    justifyContent: 'flex-start',
    flexDirection: 'column',
    backgroundColor: Colors.primaryGrayBackground,
    width: '100%',
  },
  scrollViewContainer: {
    flex: 1,
  },
  scrollViewContent: {
    backgroundColor: Colors.primaryGrayBackground,
    flexGrow: 1,
    padding: 12,
  },
  dateFilterRowRadioButton: {
    marginBottom: 12,
    borderRadius: 8,
    borderWidth: 1,
    paddingLeft: 16,
    paddingRight: 12,
    paddingTop: 16,
    paddingBottom: 16,
    flexDirection: 'column',
    backgroundColor: Colors.white,
    borderColor: Colors.graySpacer,
  },
  rowDateFilterContainer: {
    height: 44,
  },
  checkboxLabel: {
    textAlign: 'left',
    fontSize: 16,
    fontWeight: '500',
    letterSpacing: -0.25,
    color: Colors.black,
  },
  rowRadioButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'row',
  },
  renderCustomDate: {
    flexDirection: 'row',
  },
  downloadButtonContainer: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  downloadButton: {
    backgroundColor: Colors.primary,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  downloadButtonText: {
    color: Colors.white,
    fontSize: 16,
    fontWeight: '700',
  },
  dateRangeContainer: {
    ...Platform.select({
      web: {
        flexDirection: 'column',
        gap: 12,
      },
    }),
  },
});
